import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "../JaldiLab.css";
import {
  generateOrderedList,
  generateUnOrderedList,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceHtml,
  referenceStyle,
  referenceTootKit,
  subHeadingStyle,
  textDecorationStyle,
  DividerBorderStyle,
  DashedBorderStyle,
  tableCaption,
  getTextContentWithHeading,
  getCircleTypeListItem,
  sectionStyle,
} from "./ChapterList";
import {
  AccessibilityTable,
  AdaptabilityTable,
  ComfortTable,
  EfficinecyTable,
  EightTable,
  FifthTable,
  FirstTable,
  FourthTable,
  NinthTable,
  SecondTable,
  SeventhTable,
  SixthTable,
  SustainabilityTable,
  SustainabilityThirdTable,
  SustainabilityTwoTable,
  ThirdTable,
  comfortTable,
  efficinecyTable,
} from "./MoniteringTables/MoniteringTables";

function MonitoringEvaluation({ goToSection }) {
  const referencesRef = useRef(null);

  const tableHeading = {
    color: "#000",
    fontSize: "1rem",
    fontWeight: 600,
    paddingRight: "5px",
  };

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const reportTemplate = [
    {
      tableNo: "Table 21",
      tableCaption: "Template for an Audit Checklist.",
      tableData: (
        <>
          <FirstTable goToSection={goToSection} />
          <SecondTable goToSection={goToSection} />
          <ThirdTable goToSection={goToSection} />
          <FourthTable goToSection={goToSection} />
          <FifthTable goToSection={goToSection} />
          <SixthTable goToSection={goToSection} />
          <SeventhTable goToSection={goToSection} />
          <EightTable goToSection={goToSection} />
          <NinthTable goToSection={goToSection} />
        </>
      ),
    },
  ];

  const toolkitArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Infrastructure:{" "}
      </span>{" "}
      Spatial design and adjacencies of physical amenities including external
      and internal areas such as site entrance, parking, building entrance,
      Courtrooms, waiting areas, administrative offices, security systems, and
      technology infrastructure.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Environment:{" "}
      </span>{" "}
      Considering the overall atmosphere and conditions within the Complex,
      including cleanliness, safety measures, proper signage, comfort for
      visitors, and compliance with building codes and regulations.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Service Delivery Methods:{" "}
      </span>{" "}
      Assessing the accessibility and efficiency of technology and IT services
      such as video-conferencing, data and evidence storage, e-Seva Kendra
      services, etc.
    </>,
  ];
  const auditArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Planning and Commissioning:{" "}
      </span>
      This phase involves setting the scope, objectives, and parameters of the
      audit. It includes defining what aspects of the Court Complex will be
      audited, establishing goals, and securing resources and support for the
      audit.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Team Preparation:{" "}
      </span>
      A team of specialists and experts must be constituted to undertake the
      audit effectively. This team may include architects, accessibility
      auditors, occupational therapists, consultants specialising in legal
      infrastructure, and Persons with Disabilities who can provide valuable
      insights into accessibility and usability.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Briefing and Data Gathering:{" "}
      </span>
      This stage involves conducting site surveys, collecting relevant data, and
      thoroughly examining the Court Complex. It includes assessing the physical
      infrastructure, technological aspects, environmental conditions, and
      accessibility features.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Consultations and Interviews:{" "}
      </span>
      This stage involves engaging with the diverse user groups to understand
      their experiences, challenges, and needs in accessing and utilising the
      Court Complex efficiently.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Audit Methodology and Guidelines:{" "}
      </span>
      Clear methodologies and guidelines for recording data collected during the
      audit process must be developed. This may include standardised checklists,
      evaluation criteria, and procedures for assessing compliance with
      accessibility standards and other relevant regulations.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Recommendations and Reporting:{" "}
      </span>
      This stage involves analysing the gathered data and formulating
      recommendations based on the findings. These recommendations should
      address deficiencies, propose improvements, and outline actionable steps
      to enhance the Court Complex's accessibility, adaptability, comfort and
      safety, efficiency, and sustainability. A comprehensive report summarising
      the audit process, findings, and recommendations is then compiled.
    </>,
  ];

  const sequenceArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Analysing User Needs:{" "}
      </span>
      Consider the diverse needs and experiences of users including staff and
      visitors like elderly, expectant mothers, children, Persons with
      Disabilities, including mobility, vision, hearing, and cognitive
      impairments.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Mapping User Paths:{" "}
      </span>
      Identify the typical sequences of movement through the environment for
      different user groups, such as entering the facility, navigating within
      spaces, and accessing amenities.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Assessing Suitability:{" "}
      </span>
      Evaluate the suitability of the environment for various user journeys,
      considering factors such as accessibility of pathways, availability of
      assistive devices, and usability of facilities.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Incorporating Feedback:{" "}
      </span>
      Gather feedback from users including Persons with Disabilities or advocacy
      groups to ensure that the audit accurately reflects the experiences and
      perspectives of diverse users.
    </>,
  ];
  const auditSurveyArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Environment Assessment:{" "}
      </span>
      Conduct a comprehensive assessment of the physical environment, including
      entrances, pathways, signage, parking areas, sanitary provisions, service
      cores, circulations, and other facilities such as emergency preparedness,
      communication and technology assessment, etc.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Barrier Identification:{" "}
      </span>
      Identify barriers to accessibility, such as physical obstacles, ease of
      operations, inadequate signage, poor lighting, or non-compliant features.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Documentation:{" "}
      </span>
      Document audit findings using a structured format, including photographs,
      measurements, drawings and detailed descriptions of accessibility issues.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Severity Assessment:{" "}
      </span>
      Evaluate the severity and impact of identified barriers on users with
      disabilities, considering factors such as usability, safety, and
      independence.
    </>,
  ];
  const operationsMaintanceArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Implementing Remediation Measures:{" "}
      </span>
      Action plan based on audit findings to address concerns and improve
      usability. This may include modifications to physical infrastructure,
      installation of assistive devices, or updates to policies and procedures.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Training and Awareness:{" "}
      </span>
      raining for staff members on guidelines, best practices, and the
      importance of inclusive design. For further details on sensitisation,
      please refer to{" "}
      <span
        style={sectionStyle}
        onClick={() => goToSection("capacityBuilding", "sensitisation")}
      >
        Section 5.1.
      </span>
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Regular Inspections:{" "}
      </span>
      Regular inspections of the environment to identify any new barriers or
      issues that may arise over time. This proactive approach allows for prompt
      resolution of concerns and ensures ongoing compliance with standards and
      resolution of identified issues.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Maintenance Protocols:{" "}
      </span>
      Implement maintenance protocols to ensure that all features such as
      accessibility features including ramps, lifts, doors, and tactile paving,
      family-friendly facilities, washrooms for the third gender, etc., are
      functioning properly and are well-maintained.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Feedback Mechanisms:{" "}
      </span>
      Establish feedback mechanisms, such as suggestion boxes or online portals,
      to allow users to report concerns or provide input on improvements. The
      accessibility app proposed in this Handbook, may be integrated with a
      feedback mechanism (as recommended) to gather feedback from all users. For
      further details, please refer to{" "}
      <span
        style={sectionStyle}
        onClick={() => goToSection("annexes", "HeadingAnnexure9")}
      >
        Annexure 9
      </span>
      .
    </>,
  ];
  const recommendationsArray = [
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Critical:{" "}
      </span>
      Recommendations deemed critical are those necessary for ensuring safety or
      access that would otherwise be unsafe or impossible.
    </>,
    <>
      <span style={{ ...tableHeading, ...textDecorationStyle }}>
        Essential:{" "}
      </span>{" "}
      Recommendations categorised as essential represent best practices that
      should be implemented for optimal outcomes.
    </>,
  ];

  const gatherInputObject = [
    "Pre - Design Stage: to prepare the Court Complex building programme which defines the spatial and functional requirements and design considerations; and",
    "Post-Occupancy Stage: to evaluate the design of a user-centric Court Complex.",
  ];

  const tableArrayData = (data) => {
    return (
      <>
        {data &&
          data.map((elem) => {
            return (
              <div style={{ margin: "30px 0" }}>
                <p className="table-guidance-heading">
                  <span className="table-guidline-text">{elem.tableNo}: </span>
                  <br />
                  <span style={tableCaption}>{elem.tableCaption} </span>
                </p>
                {elem.tableData}
              </div>
            );
          })}
      </>
    );
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART D - IMPLEMENTATION, MONITORING & EVALUATION
        </h1>{" "}
        <h2 id="mainheading" style={h2style}>
          <span style={{ fontSize: "24px" }}>6. </span>MONITORING & EVALUATION
        </h2>
        <p style={paraStyle}>
          In order to ensure that the functions and facilities within the Court
          Complex are sufficiently meeting the needs of all users, regular
          monitoring and evaluation should be undertaken by the High Court
          Building Committees as well as the users of the Court Complex. In this
          section, the various mechanisms that can be used to undertake regular
          evaluation and monitoring exercises have been covered.
        </p>
        <h3 id="survey" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.1. </span>User Experience Survey
        </h3>
        <p style={paraStyle}>
          The Design Principles and Guidelines of this Handbook, prepared using
          feedback from users of the Court facilities and stakeholders involved
          in the Court design and management process, provide a framework for
          evaluating Court Complex designs. A User Experience Survey may be used
          to gather inputs at:
        </p>
        {getCircleTypeListItem(gatherInputObject, false, 1)}
        <p style={paraStyle}>
          The User Experience Survey template provided in this Handbook engages
          with the Court users in evaluating their perception of the building
          design and related systems. It seeks to gather feedback on how well an
          existing Court or a newly designed Court Complex aligns with the
          Design Principles and Guidelines recommended in this Handbook.
        </p>
        <p style={paraStyle}>
          The feedback from the Survey would help to chalk out improvements or
          plan retrofits/upgrades for spaces and facilities in newly designed
          Court Complexes. Prioritising and implementing changes post-evaluation
          is critical for user-centric design. The findings of the survey can be
          categorised into{" "}
          <span style={sectionStyle}>high, medium, and low priority</span> and
          issues may be prioritised accordingly. High priority issues may be
          tackled at the onset to enhance user experience. An action plan can be
          developed, responsibilities assigned, and clear timelines can be set
          for implementation accordingly. Changes made could be continuously
          iterated and validated for a user-centric approach.
        </p>
        <p style={paraStyle}>
          The User Experience Survey template given below provides a framework
          for recording user experience related to each Design Principle, by
          engaging prominent user groups.
        </p>
        <DividerBorderStyle />
        <h4 id="template" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.1.</span>A. Survey Template
        </h4>
        {/* Accessibility table */}
        <h3 style={subHeadingStyle}>I. Accessibility</h3>
        <p className="table-guidance-heading">
          <span className="table-guidline-text">Table 16:</span>
          <br />
          <span style={tableCaption}>
            User Experience Survey for Accessibility.
          </span>
        </p>
        <span>
          <AccessibilityTable />
        </span>
        {/* Adaptability table */}
        <h3 style={subHeadingStyle}>II. Adaptability</h3>
        <p className="table-guidance-heading">
          <span className="table-guidline-text">Table 17:</span>
          <br />
          <span style={tableCaption}>
            User Experience Survey for Adaptability.
          </span>
        </p>
        <span>
          <AdaptabilityTable />
        </span>
        {/* comfort and safe table */}
        <h3 style={subHeadingStyle}>III. Comfort and Safety</h3>
        <p className="table-guidance-heading">
          <span className="table-guidline-text">Table 18:</span>
          <br />
          <span style={tableCaption}>
            User Experience Survey for Comfort and Safety.
          </span>
        </p>
        <span>
          <ComfortTable />
        </span>
        {/* Efficiency Table */}
        <h3 style={subHeadingStyle}>IV. Efficiency</h3>
        <p className="table-guidance-heading">
          <span className="table-guidline-text">Table 19:</span>
          <br />
          <span style={tableCaption}>
            User Experience Survey for Efficiency.
          </span>
        </p>
        <span>
          <EfficinecyTable />
        </span>
        {/* Sustainability Table */}
        <h3 style={subHeadingStyle}>V. Sustainability</h3>
        <p className="table-guidance-heading">
          <span className="table-guidline-text"> Table 20:</span>
          <br />
          <span style={tableCaption}>
            User Experience Survey for Sustainability.
          </span>
        </p>
        <span>
          <SustainabilityTable />
        </span>
        <DashedBorderStyle />
        <h3 id="framework" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.2. </span>Post Occupancy
          Evaluation Framework: Audit Toolkit
        </h3>
        <p style={paraStyle}>
          It is recommended that an assessment of the functioning of District
          Court buildings should be carried out by the High Court after
          construction and occupation by obtaining feedback from all users.
          Typically, a post-occupancy evaluation/assessment should be conducted
          after 12-15 months of the building occupancy.
        </p>
        <p style={paraStyle}>
          An assessment/audit framework with specific objectives must be
          prepared in consultation with experts who were not involved in the
          original design or construction of the specific Court Complex. The
          concerned High Court may engage independent research
          organisations/institutions or appoint a Committee with
          multi-disciplinary experts having experience working with diverse
          stakeholders and vulnerable groups.
        </p>
        <p style={paraStyle}>
          Audits within a Court Complex play a critical role in ensuring that
          the infrastructure, environment, and service delivery methods align
          with the needs of diverse user groups. The Court Complex, being a
          multi-faceted institution, requires various elements to function
          effectively and serve its purpose. These can be carried out in either
          existing buildings or proposed developments.
        </p>
        <p style={paraStyle}>
          Audits may be undertaken of the following aspects:{" "}
        </p>
        {generateOrderedList(toolkitArray)}
        <DashedBorderStyle />
        <h4 id="componentAudit" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.2.</span>A. Components of the
          Audit
        </h4>
        <p style={paraStyle}>
          The following steps must be undertaken for planning and undertaking
          the audit of a Court Complex:
        </p>
        {generateOrderedList(auditArray)}
        <p style={paraStyle}>
          By following these steps, an audit of the Court Complex can provide a
          comprehensive assessment of its infrastructure and service delivery
          methods, ensuring alignment with the diverse needs of its users and
          facilitating necessary improvements for a more accessible, efficient
          and user-centric justice administration system.
        </p>
        <DividerBorderStyle />
        <h4 id="auditMethodolgy" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.2.</span>B. Audit Methodology
        </h4>
        <h3 id="sequence" style={subHeadingStyle}>
          I. User Journey Sequence
        </h3>
        <p style={paraStyle}>
          Understanding the user journey sequence is essential for assessing the
          suitability of the environment from the perspective of different
          users. This involves:
        </p>
        {generateUnOrderedList(sequenceArray)}
        <h3 id="auditsurvey" style={subHeadingStyle}>
          II. Audit Survey
        </h3>
        <p style={paraStyle}>
          The audit survey involves a planned and methodical assessment of the
          Court Complex. It follows a logical and sequential approach,
          encompassing:
        </p>
        {generateUnOrderedList(auditSurveyArray)}
        <h3 id="operations" style={subHeadingStyle}>
          III. Operations and Maintenance
        </h3>
        <p style={paraStyle}>
          Strategies for ongoing operations and maintenance should be
          implemented to ensure continued accessibility and usability of the
          environment.
        </p>
        {generateUnOrderedList(operationsMaintanceArray)}
        <DividerBorderStyle />
        <h4 id="recommendations" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.2.</span>C. Making
          Recommendations
        </h4>
        <p style={paraStyle}>
          The recommendations provided should further be prioritised based on
          the significance of each suggestion rather than strict adherence to
          predefined timelines such as "long-term planning" or "within the next
          6 months," which may not always align with the critical needs of the
          recommended modifications.
        </p>
        <p style={paraStyle}>
          These recommendations may be categorised as follows:
        </p>
        {generateUnOrderedList(recommendationsArray)}
        <p style={paraStyle}>
          By prioritising based on criticality and importance rather than
          arbitrary timelines, resources can be allocated more effectively,
          addressing the most urgent needs first.
        </p>
        <DividerBorderStyle />
        <h4 id="reportingTemplate" style={h3style}>
          <span style={{ fontSize: "20px" }}>6.2.</span>D. Audit Checklist and
          Reporting Template
        </h4>
        <p style={paraStyle}>
          Documenting the survey and gathering data may involve tasks such as
          note-taking, annotating plans, sketching, and completing checklists by
          using observation assessment and survey equipment including measuring
          tape, instrument for measuring gradients, door pressure gauge, light
          metres, sound pressure metres, etc.
        </p>
        <p style={paraStyle}>
          Pre-prepared checklists, if organised logically, can be highly
          beneficial. This checklist should be accompanied by detailed
          explanations, visual aids, case study references, and recommendations,
          all of which should align with the guidelines for the final report.
        </p>
        <p style={paraStyle}>
          The checklist should be tailored according to the specific type of
          audit being conducted, taking into account factors such as site
          conditions, objectives, and other relevant considerations.
        </p>
        <p style={paraStyle}>
          The Audit Checklist given below provides a framework for undertaking
          an audit of all spaces within the Court Complex. However, to ensure
          that the audit is undertaken systematically, it is recommended that
          the experts, as mentioned in{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("monitoringEvaluation", "componentAudit")
            }
          >
            {" "}
            Section 6.2.A
          </span>{" "}
          must be onboarded.
        </p>
        {tableArrayData(reportTemplate)}
      </Typography>
    </div>
  );
}

export default MonitoringEvaluation;
